import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisConfigService
} from '@campaign-portal/components-library';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { map } from 'rxjs/operators';

import { ImportRate } from '@helpers/types/models';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import {
	ImportRatesDeleteRequest,
	ImportRatesDeleteResponse,
	ImportRatesReadResponse,
	ImportRatesRequest,
	ImportRatesUpdateRequest,
	ImportRatesUpdateResponse
} from '@helpers/types/hlr';
import { ExportService } from '@helpers/api/export.service';
import { ImportRatesFieldsService } from './import-rates.fields.service';

@Injectable({
	providedIn: 'root'
})
export class ImportRatesService implements AbstractCRUDService {
	readonly entity = '';
	readonly title = '';
	readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly config: AlarisConfigService,
		private readonly fields: ImportRatesFieldsService,
		private readonly exportService: ExportService
	) {}

	read(params: ReadRequest): Observable<ReadResponse<ImportRate[]>> {
		const parsedParams: ReadRequest = this.prepareParams(params);
		return this.api.loader<ImportRatesReadResponse>('ImportRates.Read', parsedParams, this.loading$).pipe(
			map((resp) => {
				return {
					Success: !!resp.data,
					Total: resp.length,
					Data: resp.data
				};
			})
		);
	}

	update(params: ImportRatesUpdateRequest): Observable<ImportRatesUpdateResponse> {
		return this.api.loader<ImportRatesUpdateResponse>(
			params.ID ? 'ImportRates.Update' : 'ImportRates.Create',
			params,
			this.loading$
		);
	}

	delete(params: ImportRatesDeleteRequest): Observable<ImportRatesDeleteResponse> {
		return this.api.loader<ImportRatesDeleteResponse>('ImportRates.Delete', params, this.loading$);
	}

	export(total: number, params: RPCRequestParams): Observable<void> {
		params = {
			...params,
			Paging: { Skip: 0, Take: total }
		};
		return this.exportService.export(
			this.config.api,
			'ImportRates.Read',
			this.prepareParams(params),
			this.fields.headers,
			'import_rates'
		);
	}

	private prepareParams(params: RPCRequestParams): ImportRatesRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: 'provider',
			direction: 'asc',
			filter: params.Filters
				? params.Filters.map((filter) => {
					return this.parseFilterField(filter);
				}) : []
		};
	}

	private parseFilterField(filter: Filter): { key: string; value: string } {
		const variable = filter.Field;
		const value = filter.Value;
		switch (variable) {
			default:
				return { key: variable, value: value + '' };
		}
	}
}
